/**
 * SyllabusMoreMenu — 강의 머리의 [⋯] 판(설계 FE-20260912 §W05).
 *
 * 항목: 이름·설명 편집(주인) · 사본 만들기(주인) / 내 강의로 복사(공유받음) · 강의 지우기(주인).
 * 항목을 고르면 **같은 판 안에서** 화면을 바꾼다(메뉴 → 편집 · 사본 · 지우기 확인). 판 위에 판을 겹치면 두 판의
 * 바깥 클릭 규칙이 서로를 닫는다 — 시험지 폴더 메뉴(FolderTree.jsx FolderMenu)가 같은 이유로 한 상자 안에서 바꾼다.
 *
 * 사본(설계 §W05 순서 P6 → P3 → 같은 자리 줄 확인 → N2)은 작업 상태 훅의 copy 가 한다. 여기는 결과를 나눠 말한다:
 *   - 사본만 원했거나 조건까지 반영됨 → 판을 닫고 사본을 연다(옛 화면도 사본을 만들면 곧바로 열었다).
 *   - 사본은 생겼는데 조건 반영이 안 됨 → 「사본 생성됨 · 조건 반영 안 됨 — 사유」 + [사본 열기].
 *     이때 [만들기]를 다시 내놓지 않는다 — 다시 누르면 사본이 하나 더 생긴다(수용 기준 10).
 * 지우기는 window.confirm 대신 판 안 확인 문장에 줄 수를 적는다 — 무엇이 얼마나 사라지는지 누르기 전에 보이게(설계 §W05).
 */
import {
  useEffect, useRef, useState,
} from 'react';
import {
  ArrowLeft, Copy, Pencil, Trash2,
} from 'lucide-react';
import { Button } from '../common';
import FloatingPanel from './FloatingPanel';
import { describeSyllabusError } from './syllabusApi';
import { panelErrorCls } from './SharePopover';
import { CopyRecoveryControls } from './DraftRecoveryPanel';

export const NAME_MAX = 120;

const ringCls = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';
// 항목 — 시험지 폴더 메뉴(FolderTree.jsx FolderMenu)의 항목 모양
const itemCls = (danger = false) => `flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${ringCls} ${danger ? 'text-red-700 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`;
const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50';
const backCls = `shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 ${ringCls}`;

/** 강의의 줄 수 — 작업 상태 훅은 줄을 쓰기 응답으로 고쳐 나가고 헤더의 node_count 는 그대로라 items 가 더 정확하다 */
const nodeCountOf = (data) => (Array.isArray(data?.items) ? data.items.length : (data?.node_count ?? 0));

/** 메뉴 안 방향키 — 위·아래로 항목 사이, Home·End 로 처음·끝(메뉴 역할의 기본 약속) */
function onMenuKeyDown(e) {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
  const items = Array.from(e.currentTarget.querySelectorAll('[role="menuitem"]:not([disabled])'));
  if (!items.length) return;
  e.preventDefault();
  const i = items.indexOf(document.activeElement);
  let n;
  if (e.key === 'Home') n = 0;
  else if (e.key === 'End') n = items.length - 1;
  else if (e.key === 'ArrowDown') n = i < 0 ? 0 : (i + 1) % items.length;
  else n = i <= 0 ? items.length - 1 : i - 1;
  items[n].focus();
}

/** 화면 안 첫 초점 자리 — data-autofocus → 입력 → 메뉴 항목 → 단추 순. 한 선택자로 찾으면 DOM 앞쪽의 [←] 단추가 이름 칸보다 먼저 잡힌다 */
function focusFirstIn(el) {
  if (!el) return;
  const first = ['[data-autofocus]:not([disabled])', 'input:not([disabled]),textarea:not([disabled])', '[role="menuitem"]:not([disabled])', 'button:not([disabled])']
    .map((sel) => el.querySelector(sel)).find(Boolean);
  if (first) first.focus();
}

function SyllabusMoreMenu({
  anchorRef, data, owner = false, busy = false, canCopyDraft = false,
  onUpdateHeader, onCopy, onDelete, onOpenCopy, onClose, sheet = false,
  copyRecovery = null, onRetryCopyRecovery, onDiscardCopyRecovery,
}) {
  const [view, setView] = useState(copyRecovery ? 'copy' : 'menu'); // 'menu' | 'edit' | 'copy' | 'delete'
  const [name, setName] = useState(data?.name || '');
  const [description, setDescription] = useState(data?.description || '');
  const [withDraft, setWithDraft] = useState(false);
  const [copyResult, setCopyResult] = useState(copyRecovery); // { copyId, reason } — 사본은 생겼는데 조건 반영이 안 됨
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const workingRef = useRef(false);
  const aliveRef = useRef(true);
  const bodyRef = useRef(null);
  const firstView = useRef(true);

  // 효과 안에서 다시 켠다 — StrictMode(개발 서버)는 효과를 걸었다 풀었다 다시 걸어서, 끄기만 하면 꺼진 채 남아 저장 뒤에도 판이 안 닫힌다
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // 화면을 바꾸면 그 화면의 첫 자리에 초점 — 사라진 항목에 초점이 남지 않게.
  // 처음 열 때는 FloatingPanel 이 판이 보인 뒤에 첫 항목으로 옮겨 주므로 건너뛴다(숨은 판에는 초점이 붙지 않는다).
  useEffect(() => {
    if (firstView.current) { firstView.current = false; return; }
    focusFirstIn(bodyRef.current);
  }, [view]);

  // 사본 결과가 서면 누른 [만들기]가 사라진다 — 초점이 판 밖(body)으로 떨어지면 Escape·Tab 이 판에 닿지 않으니 [사본 열기]로 옮긴다
  useEffect(() => {
    if (copyResult) focusFirstIn(bodyRef.current);
  }, [copyResult]);

  useEffect(() => { setCopyResult(copyRecovery); }, [copyRecovery]);

  const go = (v) => {
    setError(null);
    if (v === 'edit') { setName(data?.name || ''); setDescription(data?.description || ''); }
    // 이 판에서 이미 사본을 만들었으면 결과를 그대로 다시 보인다 — [←] 뒤 [사본 만들기]로 돌아와 [만들기]를 또 누르면 사본이 하나 더 생긴다(수용 기준 10)
    if (v === 'copy' && !copyResult) setWithDraft(false);
    setView(v);
  };

  /** 쓰기 한 번 — 끝날 때까지 잠그고, 실패하면 판 안에 사유(409 는 describeSyllabusError 의 stale 문구) */
  const run = async (fn) => {
    if (busy || workingRef.current) return;
    workingRef.current = true;
    setWorking(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      if (aliveRef.current) setError(describeSyllabusError(e));
    } finally {
      workingRef.current = false;
      if (aliveRef.current) setWorking(false);
    }
  };

  const trimmed = name.trim();
  const nameOk = trimmed.length >= 1 && trimmed.length <= NAME_MAX;
  const headerDirty = trimmed !== (data?.name || '') || description !== (data?.description || '');

  const saveHeader = () => {
    if (!nameOk || !headerDirty) return;
    run(async () => {
      await onUpdateHeader({ name: trimmed, description });
      if (aliveRef.current) onClose('saved');
    });
  };

  const makeCopy = () => run(async () => {
    const wanted = canCopyDraft && withDraft;
    const res = await onCopy({ withDraft: wanted });
    if (res?.copyId == null) throw new Error('사본을 만들지 못했습니다.');
    if (!aliveRef.current) return;
    if (wanted && !res.applied) {
      setCopyResult({ copyId: String(res.copyId), reason: res.reason || '반영할 조건 초안을 찾지 못했습니다.' });
      return;
    }
    onClose('done');
    onOpenCopy?.(String(res.copyId));
  });

  const removeIt = () => run(async () => {
    await onDelete();
    if (aliveRef.current) onClose('done');
  });

  const copyLabel = owner ? '사본 만들기' : '내 강의로 복사';
  const nodeCount = nodeCountOf(data);

  const head = (title) => (
    <div className="mb-1 flex items-center gap-1">
      <button type="button" onClick={() => go('menu')} disabled={working} aria-label="더보기 목록으로" className={backCls}>
        <ArrowLeft className="size-3.5" />
      </button>
      <span className="min-w-0 truncate text-xs font-bold text-gray-800">{title}</span>
    </div>
  );

  let body;
  if (view === 'edit') {
    body = (
      <form onSubmit={(e) => { e.preventDefault(); saveHeader(); }} className="space-y-2 p-1">
        {head('이름·설명 편집')}
        <label className="block space-y-1">
          <span className="text-xs font-medium text-gray-500">강의 이름</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && e.nativeEvent.isComposing) e.preventDefault(); }}
            maxLength={NAME_MAX}
            disabled={working}
            aria-invalid={!nameOk}
            className={inputCls}
          />
          <span className={`block text-[11px] ${nameOk ? 'text-gray-400' : 'text-red-600'}`}>
            {nameOk ? `${trimmed.length}/${NAME_MAX}자` : `이름은 1~${NAME_MAX}자로 적어 주세요.`}
          </span>
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium text-gray-500">설명</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} disabled={working} rows={3} className={inputCls} />
        </label>
        {error && <p role="alert" className={panelErrorCls}>{error}</p>}
        <div className="flex items-center justify-end gap-1.5">
          <Button size="xs" variant="secondary" onClick={() => go('menu')} disabled={working}>취소</Button>
          <Button size="xs" type="submit" disabled={!nameOk || !headerDirty || busy} loading={working}>저장</Button>
        </div>
      </form>
    );
  } else if (view === 'copy') {
    body = (
      <div className="space-y-2 p-1">
        {head(copyLabel)}
        {copyResult ? (
          <CopyRecoveryControls
            recovery={copyRecovery || { ...copyResult, error: copyResult.reason, status: 'error' }}
            onRetry={onRetryCopyRecovery ? async (options) => {
              const res = await onRetryCopyRecovery(options);
              if (res?.applied) { setCopyResult(null); onClose('done'); onOpenCopy?.(res.copyId); }
            } : undefined}
            onDiscard={onDiscardCopyRecovery ? () => { onDiscardCopyRecovery(); setCopyResult(null); onClose('button'); } : undefined}
            onOpenCopy={(id) => { onClose('done'); onOpenCopy?.(id); }}
          />
        ) : (
          <>
            <p className="text-xs text-gray-600">
              줄과 저장된 조건까지 복제한 새 강의를 만듭니다. 사본은 개인 공유로 시작하고, 지금 강의는 바뀌지 않습니다.
            </p>
            {canCopyDraft && (
              <label className="flex cursor-pointer items-start gap-2 rounded-xl p-1 text-xs text-gray-700">
                <input type="checkbox" checked={withDraft} onChange={(e) => setWithDraft(e.target.checked)} disabled={working} className="mt-0.5 accent-primary" />
                <span className="min-w-0 flex-1">
                  지금 줄의 조건 초안도 사본에 반영
                  <span className="mt-0.5 block text-[11px] text-gray-400">사본에서 같은 자리의 행을 찾아 저장합니다. 다른 행의 초안은 옮기지 않습니다.</span>
                </span>
              </label>
            )}
            {error && <p role="alert" className={panelErrorCls}>{error}</p>}
            <div className="flex items-center justify-end gap-1.5">
              <Button size="xs" variant="secondary" onClick={() => go('menu')} disabled={working}>취소</Button>
              <Button size="xs" onClick={makeCopy} disabled={busy} loading={working}>만들기</Button>
            </div>
          </>
        )}
      </div>
    );
  } else if (view === 'delete') {
    body = (
      <div className="space-y-2 p-1">
        {head('강의 지우기')}
        <p className="text-xs text-gray-700">
          「{data?.name}」 강의를 행 {nodeCount}개까지 모두 지웁니다. 되돌릴 수 없습니다.
        </p>
        {error && <p role="alert" className={panelErrorCls}>{error}</p>}
        <div className="flex items-center justify-end gap-1.5">
          <Button size="xs" variant="secondary" onClick={() => go('menu')} disabled={working}>취소</Button>
          <Button size="xs" variant="danger" onClick={removeIt} disabled={busy} loading={working}>지우기</Button>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        <div className="truncate px-2 pt-0.5 text-xs font-bold text-gray-800" title={data?.name}>{data?.name}</div>
        <div className="px-2 pb-1 text-[10px] font-medium text-gray-400">행 {nodeCount}개</div>
        <div role="menu" aria-label="강의 더보기" tabIndex={-1} onKeyDown={onMenuKeyDown}>
          {owner && (
            <button type="button" role="menuitem" onClick={() => go('edit')} className={itemCls()}>
              <Pencil className="size-3.5 shrink-0 text-gray-400" /> 이름·설명 편집
            </button>
          )}
          <button type="button" role="menuitem" onClick={() => go('copy')} className={itemCls()}>
            <Copy className="size-3.5 shrink-0 text-gray-400" /> {copyLabel}
          </button>
          {owner && (
            <button type="button" role="menuitem" onClick={() => go('delete')} className={itemCls(true)}>
              <Trash2 className="size-3.5 shrink-0" /> 강의 지우기
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <FloatingPanel anchorRef={anchorRef} onClose={onClose} align="end" sheet={sheet} label="강의 더보기" className={sheet ? '' : 'w-72'}>
      <div ref={bodyRef}>{body}</div>
    </FloatingPanel>
  );
}

export default SyllabusMoreMenu;
